import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';

// Plan v1.5 Consistency-Check: Audience-Bullets verlinken auf Pillar-Pages
const BULLET_TO_PILLAR = {
  HANDEL: { de: '/ki-handel', en: '/ki-handel' },
  RETAIL: { de: '/ki-handel', en: '/ki-handel' },
  GASTRONOMIE: { de: '/ki-gastronomie', en: '/ki-gastronomie' },
  GASTRONOMY: { de: '/ki-gastronomie', en: '/ki-gastronomie' },
  HANDWERK: { de: '/ki-handwerk', en: '/ki-handwerk' },
  TRADES: { de: '/ki-handwerk', en: '/ki-handwerk' },
};

export default function AudienceSection() {
  const { t } = useLanguage();
  const bullets = t('audience.bullets');
  return (
    <section id="audience" data-testid="audience-section" className="bg-white py-24 md:py-32">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-100px' }} transition={{ duration: 0.7 }}
          data-testid="audience-title" className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase tracking-tight leading-[1.3] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {t('audience.title')}
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[#1f1f1f]/70 text-base md:text-lg leading-relaxed mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {t('audience.intro')}
        </motion.p>
        <div className="space-y-5 mb-10">
          {Array.isArray(bullets) && bullets.map((b, i) => {
            const isObj = b && typeof b === 'object';
            const title = isObj ? b.title : b;
            const text = isObj ? b.text : null;
            const pillarLink = title ? BULLET_TO_PILLAR[title.toUpperCase().trim()]?.de : null;
            const Inner = (
              <>
                <Check size={18} className="text-[#befa0a] mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[#1f1f1f] text-sm md:text-base font-bold uppercase tracking-wide leading-snug flex items-center gap-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {title}
                    {pillarLink && <ArrowRight size={14} className="text-[#658a07] group-hover:translate-x-1 transition-transform" />}
                  </p>
                  {text && (
                    <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{text}</p>
                  )}
                </div>
              </>
            );
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                {pillarLink ? (
                  <Link to={pillarLink} className="flex items-start gap-3 group hover:bg-[#f7f7f7] -mx-3 px-3 py-2 transition-colors">
                    {Inner}
                  </Link>
                ) : (
                  <div className="flex items-start gap-3">{Inner}</div>
                )}
              </motion.div>
            );
          })}
        </div>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}
          className="text-[#1f1f1f] text-base md:text-lg leading-relaxed font-medium" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {t('audience.closing')}
        </motion.p>
      </div>
    </section>
  );
}
